/**
 * PDF Annotation Extraction Utility
 *
 * Extracts embedded annotations from PDF files in XFDF format using PDFTron's
 * PDFNet Full API without rendering the PDF.
 */

import type { WebViewerInstance } from "@pdftron/webviewer";

import { formatBytes, getStringSizeBytes } from "@/utils/file";
import { XFDF_WRAPPER_END, XFDF_WRAPPER_START } from "@/utils/xfdf-annotations";

// ============================================================================
// Types
// ============================================================================

type CoreType = NonNullable<WebViewerInstance["Core"]>;
type PDFNetType = CoreType["PDFNet"];

/** Severity level for extraction feedback */
export type ExtractionSeverity = "info" | "warning" | "error";

export interface ExtractionResult {
  /** Array of XFDF strings, one per annotation */
  annotations: string[];
  /** Number of oversized annotations replaced with placeholders */
  replacedCount: number;
  /** User-facing message about extraction outcome */
  message?: string;
  /** Severity level for UI display */
  severity?: ExtractionSeverity;
}

// ============================================================================
// Constants
// ============================================================================

const CORE_WORKER_PATH = "/lib/webviewer/core";
const CORE_SCRIPT_PATH = "/lib/webviewer/core/webviewer-core.min.js";
const PDFNET_SCRIPT_PATH = "/lib/webviewer/core/pdf/PDFNet.js";

/** Default max size per annotation (1MB) - filters out stamps with large embedded images */
export const DEFAULT_MAX_ANNOTATION_SIZE_BYTES = 1024 * 1024;

// All XFDF annotation element types (from PDF spec / PDFNet API)
const ANNOTATION_TYPES = [
  "text",
  "highlight",
  "underline",
  "strikeout",
  "squiggly",
  "caret",
  "stamp",
  "ink",
  "freetext",
  "line",
  "square",
  "circle",
  "polygon",
  "polyline",
  "fileattachment",
  "sound",
  "movie",
  "screen",
  "redact",
  "link",
  "popup",
  "widget",
  "watermark",
  "3d",
  "custom",
] as const;

// Single-pass regex matching all annotation types
const ALL_ANNOTATIONS_REGEX = new RegExp(
  `<(${ANNOTATION_TYPES.join("|")})\\b([^>]*?)(?:/>|>([\\s\\S]*?)</\\1\\s*>)`,
  "gi",
);

// ============================================================================
// PDFNet Initialization
// ============================================================================

let initPromise: Promise<PDFNetType> | null = null;

function getGlobalPDFNet(): PDFNetType | null {
  const win = window as Window & { Core?: CoreType };
  return win.Core?.PDFNet?.runWithCleanup ? win.Core.PDFNet : null;
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}

async function initPDFNet(licenseKey?: string): Promise<PDFNetType> {
  const globalPDFNet = getGlobalPDFNet();
  if (globalPDFNet) return globalPDFNet;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    await loadScript(CORE_SCRIPT_PATH);
    await loadScript(PDFNET_SCRIPT_PATH);

    const win = window as Window & { Core?: CoreType };
    if (!win.Core?.PDFNet) {
      throw new Error("PDFNet not available after loading scripts");
    }

    win.Core.setWorkerPath(CORE_WORKER_PATH);
    if (typeof win.Core.enableFullPDF === "function") {
      win.Core.enableFullPDF();
    }

    // Initialize with license key to avoid demo mode
    if (licenseKey) {
      await win.Core.PDFNet.runWithCleanup(async () => {}, licenseKey);
    }

    return win.Core.PDFNet;
  })();

  try {
    return await initPromise;
  } catch (error) {
    initPromise = null;
    throw error;
  }
}

/**
 * Preload PDFNet during browser idle time.
 */
export function preloadPDFNet(licenseKey: string): void {
  if (getGlobalPDFNet() || initPromise) return;

  const doPreload = () => {
    initPDFNet(licenseKey).catch((error) => {
      console.warn("[PDFNet] Preload failed:", error);
    });
  };

  if ("requestIdleCallback" in window) {
    requestIdleCallback(doPreload, { timeout: 3000 });
  } else {
    setTimeout(doPreload, 100);
  }
}

// ============================================================================
// XFDF Parsing
// ============================================================================

function getAnnotsContent(xfdf: string): string | null {
  const match = /<annots\s*>([\s\S]*?)<\/annots\s*>/i.exec(xfdf);
  return match?.[1] ?? null;
}

function extractAnnotationElements(
  annotsContent: string,
): Array<{ type: string; element: string }> {
  const annotations: Array<{ type: string; element: string }> = [];
  ALL_ANNOTATIONS_REGEX.lastIndex = 0;

  let match = ALL_ANNOTATIONS_REGEX.exec(annotsContent);
  while (match) {
    annotations.push({ type: match[1].toLowerCase(), element: match[0] });
    match = ALL_ANNOTATIONS_REGEX.exec(annotsContent);
  }

  return annotations;
}

// ============================================================================
// Oversized Annotation Processing
// ============================================================================

/**
 * Get human-readable type name for display in warning message.
 */
function getReadableTypeName(type: string): string {
  const typeNames: Record<string, string> = {
    stamp: "Stamp image",
    fileattachment: "File attachment",
    ink: "Drawing data",
    freetext: "Text content",
    sound: "Sound data",
    movie: "Movie data",
    screen: "Screen annotation",
    "3d": "3D model",
  };
  return typeNames[type] || "Embedded data";
}

/**
 * Escape special XML characters in text content.
 */
function escapeXml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

/**
 * Strip large binary data from an annotation element.
 * Removes base64-encoded data, embedded files, and large content while preserving structure.
 *
 * Common patterns for large data in XFDF:
 * - <data>base64...</data> - embedded file data
 * - <imagedata>base64...</imagedata> - stamp images
 * - <defaultappearance>...</defaultappearance> - appearance streams
 * - <appearance>...</appearance> - custom appearances
 * - Large inline base64 in attributes
 * - <trn-custom-data bytes="..."> with trn-attachments (Apryse file attachment replies)
 */
function stripBinaryData(element: string, annotationType: string): string {
  let stripped = element;

  // Remove <data>...</data> elements (file attachments)
  stripped = stripped.replace(/<data\b[^>]*>[\s\S]*?<\/data\s*>/gi, "");

  // Remove <imagedata>...</imagedata> elements (stamp images)
  stripped = stripped.replace(/<imagedata\b[^>]*>[\s\S]*?<\/imagedata\s*>/gi, "");

  // Remove <appearance>...</appearance> elements (custom appearances)
  stripped = stripped.replace(/<appearance\b[^>]*>[\s\S]*?<\/appearance\s*>/gi, "");

  // Remove <defaultappearance>...</defaultappearance> elements
  stripped = stripped.replace(/<defaultappearance\b[^>]*>[\s\S]*?<\/defaultappearance\s*>/gi, "");

  // Remove large base64 data in attributes (anything > 1000 chars that looks like base64)
  // This catches inline image data in attributes like imagedata="..."
  stripped = stripped.replace(
    /\b(imagedata|data|stream)\s*=\s*["'][A-Za-z0-9+/=\s]{1000,}["']/gi,
    "",
  );

  // For file attachments, remove the file attribute so WebViewer doesn't render a broken link
  if (annotationType === "fileattachment") {
    stripped = stripped.replace(/\bfile\s*=\s*["'][^"']*["']\s*/gi, "");
  }

  // Handle Apryse trn-custom-data with trn-attachments (file attachment replies)
  // The bytes attribute contains JSON with trn-attachments array containing hex-encoded file data
  stripped = stripTrnAttachments(stripped);

  return stripped;
}

/**
 * Strip trn-attachments from trn-custom-data while preserving other custom data.
 * File attachment replies store file data in trn-custom-data bytes attribute as JSON.
 *
 * Format: <trn-custom-data bytes="{&quot;trn-attachments&quot;:&quot;[...]&quot;,...}" />
 */
function stripTrnAttachments(element: string): string {
  // Match trn-custom-data elements with bytes attribute
  const trnCustomDataRegex = /<trn-custom-data\s+bytes="([^"]*)"[^>]*\/>/gi;

  return element.replace(trnCustomDataRegex, (match, bytesValue) => {
    try {
      // Decode HTML entities in the bytes attribute
      const decodedBytes = bytesValue
        .replace(/&quot;/g, '"')
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&apos;/g, "'");

      const customData = JSON.parse(decodedBytes);

      // Check if trn-attachments exists
      if (!customData["trn-attachments"]) {
        return match; // No attachments, return unchanged
      }

      // Remove trn-attachments, keep other data
      delete customData["trn-attachments"];

      // If no other data remains, remove the element entirely
      if (Object.keys(customData).length === 0) {
        return "";
      }

      // Re-encode the remaining data
      const newBytes = JSON.stringify(customData)
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/'/g, "&apos;");

      return `<trn-custom-data bytes="${newBytes}" />`;
    } catch {
      // If parsing fails, return unchanged
      return match;
    }
  });
}

/**
 * Extract the filename from a fileattachment element or trn-attachments data.
 */
function extractFilename(element: string): string | null {
  // First try standard file="filename.ext" attribute
  const fileMatch = /\bfile\s*=\s*["']([^"']+)["']/i.exec(element);
  if (fileMatch) {
    return fileMatch[1];
  }

  // Try to extract from trn-attachments in trn-custom-data (file attachment replies)
  const trnMatch = /trn-attachments[^[]*\[\{[^}]*"name"\s*:\s*"([^"]+)"/i.exec(element);
  return trnMatch?.[1] ?? null;
}

/**
 * Check if an element contains trn-attachments (file attachment reply).
 */
function hasTrnAttachments(element: string): boolean {
  return /trn-attachments/.test(element);
}

/**
 * Add or update the contents element with a warning message.
 * Replaces any existing content with just the warning.
 * For file attachments (including replies), includes the filename in the warning.
 *
 * Note: XFDF can have both <contents> and <contents-richtext> elements.
 * We remove <contents-richtext> entirely and replace <contents> with the warning.
 */
function addWarningToContents(
  element: string,
  annotationType: string,
  maxSizeBytes: number,
): string {
  const readableSize = formatBytes(maxSizeBytes);

  // Determine if this is a file attachment (standard or reply)
  const isFileAttachment = annotationType === "fileattachment" || hasTrnAttachments(element);
  const readableType = isFileAttachment ? "File attachment" : getReadableTypeName(annotationType);

  // For file attachments, try to include the filename
  let warningMessage: string;
  if (isFileAttachment) {
    const filename = extractFilename(element);
    if (filename) {
      warningMessage = `⚠️ ${readableType} "${filename}" not imported (exceeded ${readableSize} limit)`;
    } else {
      warningMessage = `⚠️ ${readableType} not imported (exceeded ${readableSize} limit)`;
    }
  } else {
    warningMessage = `⚠️ ${readableType} not imported (exceeded ${readableSize} limit)`;
  }

  let result = element;

  // Remove <contents-richtext> elements entirely (they can contain complex HTML)
  result = result.replace(/<contents-richtext\b[^>]*>[\s\S]*?<\/contents-richtext\s*>/gi, "");

  // Check if there's an existing <contents> element (exact match, not contents-richtext)
  // Use negative lookahead to ensure we don't match contents-richtext
  const contentsMatch = /<contents>([^<]*(?:<(?!\/contents>)[^<]*)*)<\/contents\s*>/i.exec(result);

  if (contentsMatch) {
    // Replace contents with just the warning message
    const newContents = `<contents>${escapeXml(warningMessage)}</contents>`;
    return result.replace(contentsMatch[0], newContents);
  }

  // No existing contents - add before closing tag
  // Handle both self-closing tags and regular closing tags
  const selfClosingMatch = /\/>\s*$/i.exec(result);
  if (selfClosingMatch) {
    // Convert self-closing to regular tag with contents
    const tagMatch = /^<(\w+)/.exec(result);
    const tagName = tagMatch?.[1] || "annotation";
    return result.replace(
      selfClosingMatch[0],
      `><contents>${escapeXml(warningMessage)}</contents></${tagName}>`,
    );
  }

  // Regular closing tag - insert contents before it
  const closingTagMatch = /<\/(\w+)\s*>\s*$/i.exec(result);
  if (closingTagMatch) {
    return result.replace(
      closingTagMatch[0],
      `<contents>${escapeXml(warningMessage)}</contents>${closingTagMatch[0]}`,
    );
  }

  // Fallback - shouldn't happen with valid XML
  return result;
}

/**
 * Process an oversized annotation by stripping binary data and adding a warning.
 * Preserves the original annotation structure, author, date, position, etc.
 */
function processOversizedAnnotation(
  element: string,
  annotationType: string,
  maxSizeBytes: number,
): string {
  // First strip the binary data
  let processed = stripBinaryData(element, annotationType);

  // Add warning message to contents
  processed = addWarningToContents(processed, annotationType, maxSizeBytes);

  return processed;
}

// ============================================================================
// Main Extraction Function
// ============================================================================

/**
 * Extract annotations from a PDF file.
 * Returns individual annotations wrapped in XFDF format, ready for DB storage.
 * Oversized annotations have their binary data stripped and a warning message added.
 */
export async function extractAnnotationsFromPdf(
  file: File,
  licenseKey: string,
  maxAnnotationSizeBytes = DEFAULT_MAX_ANNOTATION_SIZE_BYTES,
): Promise<ExtractionResult> {
  try {
    const PDFNet = await initPDFNet(licenseKey);
    const arrayBuffer = await file.arrayBuffer();

    let rawXfdf: string | null = null;
    await PDFNet.runWithCleanup(async () => {
      const doc = await PDFNet.PDFDoc.createFromBuffer(arrayBuffer);
      await doc.initSecurityHandler();
      const fdfDoc = await doc.fdfExtract(PDFNet.PDFDoc.ExtractFlag.e_annots_only);
      rawXfdf = await fdfDoc.saveAsXFDFAsString();
    }, licenseKey);

    if (!rawXfdf) {
      return { annotations: [], replacedCount: 0 };
    }

    const annotsContent = getAnnotsContent(rawXfdf);
    if (!annotsContent) {
      return { annotations: [], replacedCount: 0 };
    }

    const rawAnnotations = extractAnnotationElements(annotsContent);
    const annotations: string[] = [];
    let replacedCount = 0;

    for (const raw of rawAnnotations) {
      const xfdf = `${XFDF_WRAPPER_START}${raw.element}${XFDF_WRAPPER_END}`;
      const sizeBytes = getStringSizeBytes(xfdf);

      if (sizeBytes > maxAnnotationSizeBytes) {
        // Strip binary data and add warning message, preserving original annotation structure
        const processedElement = processOversizedAnnotation(
          raw.element,
          raw.type,
          maxAnnotationSizeBytes,
        );
        const processedXfdf = `${XFDF_WRAPPER_START}${processedElement}${XFDF_WRAPPER_END}`;
        annotations.push(processedXfdf);
        replacedCount++;
      } else {
        annotations.push(xfdf);
      }
    }

    // Determine severity and message based on results
    const totalFound = annotations.length; // All annotations are now included (originals + placeholders)

    if (totalFound === 0) {
      // No annotations in PDF - no message needed
      return { annotations: [], replacedCount: 0 };
    }

    if (replacedCount === 0) {
      // All annotations extracted successfully
      return {
        annotations,
        replacedCount: 0,
        message: `Found ${annotations.length} embedded annotation${annotations.length !== 1 ? "s" : ""}.`,
        severity: "info",
      };
    }

    // Some annotations were replaced with placeholders
    const originalCount = annotations.length - replacedCount;
    if (originalCount === 0) {
      // All were replaced
      return {
        annotations,
        replacedCount,
        message: `Found ${replacedCount} embedded annotation${replacedCount !== 1 ? "s" : ""} (all replaced with placeholders due to size).`,
        severity: "warning",
      };
    }

    // Mix of originals and placeholders
    return {
      annotations,
      replacedCount,
      message: `Found ${annotations.length} embedded annotation${annotations.length !== 1 ? "s" : ""} (${replacedCount} replaced with placeholder${replacedCount !== 1 ? "s" : ""} due to size).`,
      severity: "warning",
    };
  } catch (error) {
    return {
      annotations: [],
      replacedCount: 0,
      message: `Annotation extraction failed: ${error instanceof Error ? error.message : "Unknown error"}`,
      severity: "error",
    };
  }
}
